import {Injectable} from '@angular/core';
import {Capacitor} from '@capacitor/core';
import {ActionPerformed, PushNotifications, PushNotificationSchema, Token} from '@capacitor/push-notifications';
import {UserHttpService} from '@core/services/user-http.service';
import {BaseOccpHttpService} from '@core/services/base-occp-http.service';
import {
  Notifications,
  PushTokenPlatform,
  PushTokenProvider,
  RegisterPushTokenRequest,
  UnregisterPushTokenRequest,
} from 'spotcharge-types';
import {Router} from '@angular/router';
import {firstValueFrom} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  notificationInterval: any;
  private currentToken: string | null = null;

  constructor(
    private userHttpService: UserHttpService,
    private occpHttp: BaseOccpHttpService,
    private router: Router,
  ) {
  }

  async initPush() {
    if (Capacitor.getPlatform() !== 'web') {
      await this.registerPush();
    }
  }

  async unregisterCurrentToken(): Promise<void> {
    if (!this.currentToken) {
      return;
    }
    const body: UnregisterPushTokenRequest = {
      token: this.currentToken,
      provider: PushTokenProvider.Fcm,
    };
    try {
      await firstValueFrom(this.occpHttp.delete('api/consumer/me/push-tokens', body));
    } catch (error) {
      console.error('Failed to unregister push token', error);
    } finally {
      this.currentToken = null;
    }
  }

  private async registerPush() {
    const permission = await PushNotifications.requestPermissions();
    if (permission.receive === 'granted') {
      await PushNotifications.register();

      await PushNotifications.addListener('registration', async (token: Token) => {
        console.log('My token: ' + JSON.stringify(token));
        if (token) {
          const user = await this.userHttpService.getUser();
          if (user) {
            this.currentToken = token.value;
            const body: RegisterPushTokenRequest = {
              token: token.value,
              provider: PushTokenProvider.Fcm,
              platform: this.platform(),
            };
            await firstValueFrom(this.occpHttp.post('api/consumer/me/push-tokens', body));
            console.log('token stored successfully.');
          }
        }
      });
    }

    await PushNotifications.addListener('registrationError', (error: any) => {
      console.log('Error: ' + JSON.stringify(error));
    });

    await PushNotifications.addListener(
      'pushNotificationReceived',
      async (notification: PushNotificationSchema) => {
        console.log('Push received: ', notification);
      }
    );

    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      async (notification: ActionPerformed) => {
        const data = notification.notification.data;
        console.log('Action performed: ' + JSON.stringify(notification.notification));
        if (data) {
          console.log('notification detail:', data);
          if (data.type === Notifications.scheduleBooking) {
            await this.router.navigate(['/home-tabs/tabs/schedule']);
          }
          if (data.type === Notifications.completeCharging) {
            if (data.sessionId) {
              await this.router.navigate(['/session', data.sessionId]);
            }
          }
        }
      }
    );
  }

  private platform(): PushTokenPlatform {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') {
      return PushTokenPlatform.Ios;
    }
    if (platform === 'android') {
      return PushTokenPlatform.Android;
    }
    return PushTokenPlatform.Web;
  }
}
